import React, { useState, useEffect } from 'react';
import { Search, Filter, Calendar, MapPin, Trophy, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { GameCard } from '@/components/common/GameCard';
import { VideoTheater } from '@/components/common/VideoTheater';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';

const REGIONS = [
  { value: 'west',    label: 'West' },
  { value: 'east',    label: 'East' },
  { value: 'central', label: 'Central' },
  { value: 'south',   label: 'South' },
];

export const FindGamePage = () => {
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Filter states (categorized by Region → Conference → Club, not Sport)
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedConference, setSelectedConference] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [selectedGame, setSelectedGame] = useState(null);
  const [keyword, setKeyword] = useState('');

  // Filtered options based on selections
  const [filteredConferences, setFilteredConferences] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);

  const currentYear = new Date().getFullYear();
  const seasons = [
    { value: 'all', label: 'All Seasons' },
    { value: `${currentYear}`, label: `${currentYear}` },
    { value: `${currentYear - 1}`, label: `${currentYear - 1}` },
    { value: `${currentYear - 2}`, label: `${currentYear - 2}` },
    { value: `${currentYear - 3}`, label: `${currentYear - 3}` },
  ];

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [leaguesRes, teamsRes] = await Promise.all([
          api.getLeagues(),
          api.getTeams(),
        ]);
        setLeagues(leaguesRes.data);
        setFilteredConferences(leaguesRes.data);
        setTeams(teamsRes.data);
        setFilteredTeams(teamsRes.data);
      } catch (error) {
        console.error('Error loading filter data:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    loadData();
  }, []);

  // Region → cascade to Conferences (leagues with that region)
  useEffect(() => {
    if (selectedRegion && selectedRegion !== 'all') {
      const regionLeagues = leagues.filter(l => l.region === selectedRegion);
      setFilteredConferences(regionLeagues);
      if (selectedConference !== 'all' && !regionLeagues.find(l => l.id === selectedConference)) {
        setSelectedConference('all');
      }
    } else {
      setFilteredConferences(leagues);
    }
  }, [selectedRegion, leagues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Conference → cascade to Clubs (teams)
  useEffect(() => {
    if (selectedConference && selectedConference !== 'all') {
      const conferenceTeams = teams.filter(t =>
        t.league_id === selectedConference ||
        (t.league_ids && t.league_ids.includes(selectedConference))
      );
      setFilteredTeams(conferenceTeams);
      if (selectedTeam !== 'all' && !conferenceTeams.find(t => t.id === selectedTeam)) {
        setSelectedTeam('all');
      }
    } else if (selectedRegion && selectedRegion !== 'all') {
      const regionLeagueIds = filteredConferences.map(l => l.id);
      const regionTeams = teams.filter(t =>
        regionLeagueIds.includes(t.league_id) ||
        (t.league_ids && t.league_ids.some(id => regionLeagueIds.includes(id)))
      );
      setFilteredTeams(regionTeams);
    } else {
      setFilteredTeams(teams);
    }
  }, [selectedConference, selectedRegion, filteredConferences, teams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedConference && selectedConference !== 'all') params.league_id = selectedConference;
      if (selectedTeam && selectedTeam !== 'all') params.team_id = selectedTeam;

      const res = await api.getGames(params);
      let filteredGames = res.data;

      // Region filter (if no specific conference selected)
      if (selectedRegion && selectedRegion !== 'all' && (selectedConference === 'all')) {
        const regionLeagueIds = filteredConferences.map(l => l.id);
        filteredGames = filteredGames.filter(g => regionLeagueIds.includes(g.league_id));
      }

      // Show only games that have a stream (legacy video_sources OR new stream_provider)
      filteredGames = filteredGames.filter(game =>
        (game.video_sources && game.video_sources.length > 0) ||
        (game.stream_provider && game.stream_url)
      );

      // Season filter
      if (selectedSeason && selectedSeason !== 'all') {
        filteredGames = filteredGames.filter(game => {
          const gameYear = new Date(game.scheduled_time).getFullYear().toString();
          return gameYear === selectedSeason;
        });
      }

      // Keyword filter (matches home/away team name, league name, venue — case-insensitive)
      const kw = keyword.trim().toLowerCase();
      if (kw) {
        filteredGames = filteredGames.filter(game => {
          const hay = [
            game.home_team_name,
            game.away_team_name,
            game.league_name,
            game.venue,
          ].filter(Boolean).join(' ').toLowerCase();
          return hay.includes(kw);
        });
      }

      setGames(filteredGames);
    } catch (error) {
      console.error('Error searching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedRegion('all');
    setSelectedConference('all');
    setSelectedTeam('all');
    setSelectedSeason('all');
    setKeyword('');
    setGames([]);
  };

  const getActiveFilters = () => {
    const filters = [];
    if (selectedRegion && selectedRegion !== 'all') {
      const r = REGIONS.find(x => x.value === selectedRegion);
      if (r) filters.push(r.label);
    }
    if (selectedConference && selectedConference !== 'all') {
      const league = leagues.find(l => l.id === selectedConference);
      if (league) filters.push(league.name);
    }
    if (selectedTeam && selectedTeam !== 'all') {
      const team = teams.find(t => t.id === selectedTeam);
      if (team) filters.push(team.name);
    }
    if (selectedSeason && selectedSeason !== 'all') filters.push(selectedSeason);
    return filters;
  };

  return (
    <div data-testid="find-game-page">
      <PageHeroBanner
        pageSlug="find-game"
        fallbackTitle="Find a Stream"
        fallbackSubtitle="Browse WPSL match streams by region, conference, and club"
      />

      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Search Filters */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-8">
            <div className="flex items-center gap-2 mb-6">
              <Filter className="w-5 h-5 text-wpsl-blue" />
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Find Your Stream
              </h2>
            </div>

            {/* Keyword search bar — works in tandem with the filters below */}
            <div className="mb-5">
              <label className="block text-sm text-gray-400 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Search by keyword
              </label>
              <div className="flex items-center bg-gray-700 border border-gray-600 focus-within:border-sky-500 rounded-md px-3 py-2 transition-colors">
                <Search className="w-4 h-4 text-gray-400 shrink-0 mr-2" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="Team name, conference, venue…"
                  className="flex-1 bg-transparent text-white outline-none placeholder:text-gray-500 text-sm"
                  data-testid="find-stream-keyword"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Region Dropdown — replaces Sport */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Region
                </label>
                <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="filter-region">
                    <SelectValue placeholder="All Regions" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="all" className="text-gray-300">All Regions</SelectItem>
                    {REGIONS.map(r => (
                      <SelectItem key={r.value} value={r.value} className="text-white">
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Conference Dropdown — renamed from Competition */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Trophy className="w-4 h-4 inline mr-1" />
                  Conference
                </label>
                <Select value={selectedConference} onValueChange={setSelectedConference}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="filter-conference">
                    <SelectValue placeholder="All Conferences" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                    <SelectItem value="all" className="text-gray-300">All Conferences</SelectItem>
                    {filteredConferences.map(league => (
                      <SelectItem key={league.id} value={league.id} className="text-white">
                        {league.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Club Dropdown */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Users className="w-4 h-4 inline mr-1" />
                  Club
                </label>
                <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="filter-club">
                    <SelectValue placeholder="All Clubs" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                    <SelectItem value="all" className="text-gray-300">All Clubs</SelectItem>
                    {filteredTeams.map(team => (
                      <SelectItem key={team.id} value={team.id} className="text-white">
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Season Dropdown */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Season
                </label>
                <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="filter-season">
                    <SelectValue placeholder="All Seasons" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {seasons.map(season => (
                      <SelectItem key={season.value} value={season.value} className="text-white">
                        {season.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Action Buttons - WPSL pill style */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleSearch}
                className="bg-wpsl-red hover:bg-wpsl-blue text-white font-bold rounded-full px-7 py-5 tracking-wide transition-colors"
                style={{ letterSpacing: '0.5px' }}
                disabled={loading || initialLoading}
                data-testid="search-games-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Searching...' : 'SEARCH'}
              </Button>
              <Button
                onClick={handleClear}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700 rounded-full px-7"
              >
                Clear Filters
              </Button>
            </div>

            {/* Active Filters Display */}
            {getActiveFilters().length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-gray-400 text-sm">Active filters:</span>
                {getActiveFilters().map((filter) => (
                  <span
                    key={filter}
                    className="text-sm px-3 py-1 rounded-full border"
                    style={{
                      backgroundColor: 'rgba(0, 100, 166, 0.2)',
                      borderColor: 'rgba(0, 100, 166, 0.4)',
                      color: '#cce4f4',
                    }}
                  >
                    {filter}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Results Section */}
          {games.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">
                  {games.length} Stream{games.length !== 1 ? 's' : ''} Available
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {games.map(game => (
                  <GameCard key={game.id} game={game} onWatch={setSelectedGame} />
                ))}
              </div>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-800/50 rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-video bg-gray-700" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-1/2" />
                    <div className="h-5 bg-gray-700 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-gray-800/50 rounded-xl p-12 max-w-md mx-auto border border-gray-700">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-2">Find a stream to watch</p>
                <p className="text-gray-500 text-sm">
                  Filter by region, conference, club, or season to browse WPSL match streams and replays.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <VideoTheater game={selectedGame} isOpen={!!selectedGame} onClose={() => setSelectedGame(null)} />
    </div>
  );
};

export default FindGamePage;
